import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Icon from '@mui/material/Icon';
import { Autocomplete, Chip, Stack, TextField } from '@mui/material';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';

function statusUi(theme, testResult) {
  if (!testResult) return null;

  const ok = !!testResult.ok;
  const hasSessions = testResult.hasSessions;
  const status = String(testResult.status || '');

  // başarı ama seans yok => warning
  const variant = ok && hasSessions === false ? 'warning' : ok ? 'success' : 'error';

  const palette =
    variant === 'success'
      ? theme.palette.success
      : variant === 'warning'
      ? theme.palette.warning
      : theme.palette.error;

  const bg = alpha(palette.main, theme.palette.mode === 'dark' ? 0.16 : 0.12);
  const border = alpha(palette.main, theme.palette.mode === 'dark' ? 0.28 : 0.26);

  const icon = variant === 'success' ? 'check_circle' : variant === 'warning' ? 'info' : 'error';

  const title =
    variant === 'success'
      ? 'Başarılı'
      : variant === 'warning'
      ? 'Başarılı (Uyarı var)'
      : 'Başarısız';

  return { variant, bg, border, icon, title, status };
}

export default function TestMailModal({
  open,
  onClose,
  defaultEmail,
  staffOptions = [],
  defaultStaffId,
  blockingErrors = [],
  warnings = [],
  loading = false,
  onConfirm,
  testResult = null, // ✅ yeni
}) {
  const theme = useMuiTheme();

  const [email, setEmail] = React.useState(defaultEmail || '');
  const [staffId, setStaffId] = React.useState(defaultStaffId ?? null);

  React.useEffect(() => {
    if (!open) return;
    setEmail(defaultEmail || '');
    setStaffId(defaultStaffId ?? null);
  }, [open, defaultEmail, defaultStaffId]);

  const selectedStaff = staffOptions.find((s) => String(s.id) === String(staffId)) || null;

  const border = alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.14 : 0.12);

  const emailOk = String(email || '')
    .trim()
    .includes('@');
  const staffOk =
    !!selectedStaff && selectedStaff.working === true && selectedStaff.active !== false;
  const blockingOk = (blockingErrors?.length ?? 0) === 0;

  // ✅ BUTON KİLİDİ: sadece form geçersizse / loading ise
  // blocking varsa kullanıcı zaten modal içinde görsün ama confirm’e basamasın
  const canConfirm = !loading && emailOk && staffId != null && staffOk && blockingOk;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm?.({
      toEmail: String(email || '').trim(),
      staffId: selectedStaff.id,
    });
  };

  const ui = statusUi(theme, testResult);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 900, pb: 1 }}>Test Maili</DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <MDTypography variant="caption" sx={{ opacity: 0.82 }}>
          Test, ayarlarınızı kontrol eder ve sonucu burada gösterir.
        </MDTypography>

        {/* ✅ 1) CHECK sonucu: blocking + warnings (MODAL İÇİNDE) */}
        {loading || blockingErrors?.length || warnings?.length ? (
          <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: 'wrap', gap: 0.75 }}>
            {loading && <Chip size="small" label="Kontrol ediliyor..." variant="outlined" />}
            {(blockingErrors || []).map((x) => (
              <Chip key={x} size="small" label={x} color="error" variant="outlined" />
            ))}
            {(warnings || []).map((x) => (
              <Chip key={x} size="small" label={x} color="warning" variant="outlined" />
            ))}
          </Stack>
        ) : null}

        {/* ✅ 2) TEST sonucu: backend message (MODAL İÇİNDE TEK KUTU) */}
        {ui && (
          <MDBox
            sx={{
              mt: 1.25,
              p: 1.15,
              borderRadius: 2.25,
              border: `1px solid ${ui.border}`,
              bgcolor: ui.bg,
              display: 'grid',
              gap: 0.4,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Icon sx={{ fontSize: 18 }}>{ui.icon}</Icon>
              <MDTypography variant="caption" sx={{ fontWeight: 900 }}>
                {ui.title}
              </MDTypography>
              {ui.status ? (
                <MDTypography variant="caption" sx={{ opacity: 0.72 }}>
                  • {ui.status}
                </MDTypography>
              ) : null}
            </Stack>

            {!!testResult?.message && (
              <MDTypography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
                {testResult.message}
              </MDTypography>
            )}

            {typeof testResult?.sessionCount === 'number' && (
              <MDTypography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                Bugün seçilen çalışanın seans sayısı: <b>{testResult.sessionCount}</b>
              </MDTypography>
            )}
          </MDBox>
        )}

        <Divider sx={{ my: 1.5 }} />

        <MDTypography variant="caption" sx={{ fontWeight: 900, mb: 0.6, display: 'block' }}>
          Hangi çalışanın sabah planı?
        </MDTypography>

        <Autocomplete
          size="small"
          options={staffOptions}
          value={selectedStaff}
          onChange={(e, v) => setStaffId(v?.id ?? null)}
          getOptionLabel={(o) => o?.name || `#${o?.id}`}
          isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Çalışan seç"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.25, fontWeight: 800 } }}
            />
          )}
        />

        {staffId != null && !staffOk && (
          <MDTypography variant="caption" sx={{ mt: 0.75, color: theme.palette.warning.main }}>
            Seçilen çalışan aktif/çalışıyor değil. (Test için aktif + working olmalı)
          </MDTypography>
        )}

        <MDTypography
          variant="caption"
          sx={{ fontWeight: 900, mt: 1.5, mb: 0.6, display: 'block' }}
        >
          Gönderilecek Email (override)
        </MDTypography>

        <MDInput
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="ornek@domain.com"
          sx={{ '& .MuiInputBase-root': { borderRadius: 2.25, fontWeight: 800 } }}
        />

        {!emailOk && (
          <MDTypography variant="caption" sx={{ mt: 0.75, color: theme.palette.warning.main }}>
            Geçerli bir email gir.
          </MDTypography>
        )}

        {!blockingOk && (
          <MDTypography variant="caption" sx={{ mt: 0.75, color: theme.palette.error.main }}>
            Test kilitli: {blockingErrors.join(', ')}
          </MDTypography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 1.5, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 900 }}
        >
          Kapat
        </Button>

        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!canConfirm}
          sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 900, px: 2.1 }}
          startIcon={<Icon fontSize="small">{loading ? 'hourglass_top' : 'send'}</Icon>}
        >
          Test et
        </Button>
      </DialogActions>
    </Dialog>
  );
}
